import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage, dateLocale } from './i18n';
import './AdminEventManager.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminEventManager = ({ onClose }) => {
  const { language } = useLanguage();
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('completed'); // Show completed events by default
  const [view, setView] = useState('events'); // 'events' | 'trash'
  const [trashEvents, setTrashEvents] = useState([]);

  const translations = {
    kk: {
      title: 'Оқиғаларды басқару',
      searchPlaceholder: 'Оқиғаны іздеу...',
      statusFilter: 'Мәртебе бойынша сүзгі',
      allStatuses: 'Барлық мәртебелер',
      completed: 'Орындалды',
      finalCompleted: 'Соңғы орындалды',
      inProgress: 'Орындалуда',
      revertStatus: 'Мәртебені қайтару',
      finalComplete: 'Соңғы орындау',
      deleteEvent: 'Жою',
      close: 'Жабу',
      confirmRevert: 'Шынымен де мәртебені қайтарғыңыз келе ме?',
      confirmFinalComplete: 'Шынымен де оқиғаны соңғы рет орындағыңыз келе ме?',
      confirmDelete: 'Оқиға себетке жылжытылады (30 күн сақталады). Жалғастыру керек пе?',
      deleted: 'Оқиға себетке жылжытылды',
      eventsTab: 'Оқиғалар',
      trash: 'Себет',
      restore: 'Қалпына келтіру',
      purge: 'Біржола жою',
      confirmRestore: 'Оқиғаны қалпына келтіру керек пе?',
      confirmPurge: 'Оқиға БІРЖОЛА жойылады. Жалғастыру керек пе?',
      restored: 'Оқиға қалпына келтірілді',
      purged: 'Оқиға біржола жойылды',
      deletedAt: 'Жойылды',
      trashNote: 'Себеттегі оқиғалар 30 күн сақталады, кейін автоматты түрде жойылады',
      trashEmpty: 'Себет бос',
      newStatus: 'Жаңа',
      assigned: 'Тағайындалды',
      rejected: 'Қабылданбады',
      noEvents: 'Оқиғалар табылмады',
      loading: 'Жүктелуде...',
      loadError: 'Оқиғаларды жүктеу қатесі',
      statusReverted: 'Оқиға мәртебесі сәтті болдырылмады',
      revertError: 'Мәртебені болдырмау қатесі',
      finalCompletedMsg: 'Оқиға біржола аяқталды',
      finalCompleteError: 'Біржола аяқтау қатесі',
      deleteError: 'Оқиғаны жою қатесі',
      restoreError: 'Оқиғаны қалпына келтіру қатесі',
      purgeError: 'Біржола жою қатесі',
      category: 'Санат',
      addressLabel: 'Мекенжай',
      notSpecified: 'Көрсетілмеген',
      priorityLabel: 'Маңыздылық',
      createdLabel: 'Жасалды',
      assignedLabel: 'Тағайындалды',
      deletedBy: 'Жойған',
      finalCompletedLabel: 'Біржола аяқталды'
    },
    ru: {
      title: 'Управление событиями',
      searchPlaceholder: 'Поиск события...',
      statusFilter: 'Фильтр по статусу',
      allStatuses: 'Все статусы',
      completed: 'Выполнено службой',
      finalCompleted: 'Окончательно завершено',
      inProgress: 'В работе',
      revertStatus: 'Отменить статус',
      finalComplete: 'Завершить окончательно',
      deleteEvent: 'Удалить',
      close: 'Закрыть',
      confirmRevert: 'Вы уверены, что хотите отменить статус выполнения?',
      confirmFinalComplete: 'Вы уверены, что хотите окончательно завершить событие?',
      confirmDelete: 'Событие будет перемещено в корзину (хранится 30 дней). Продолжить?',
      deleted: 'Событие перемещено в корзину',
      eventsTab: 'События',
      trash: 'Корзина',
      restore: 'Восстановить',
      purge: 'Удалить навсегда',
      confirmRestore: 'Восстановить событие?',
      confirmPurge: 'Событие будет удалено НАВСЕГДА. Продолжить?',
      restored: 'Событие восстановлено',
      purged: 'Событие удалено навсегда',
      deletedAt: 'Удалено',
      trashNote: 'События в корзине хранятся 30 дней, затем удаляются автоматически',
      trashEmpty: 'Корзина пуста',
      newStatus: 'Новое',
      assigned: 'Назначено',
      rejected: 'Отклонено',
      noEvents: 'События не найдены',
      loading: 'Загрузка...',
      loadError: 'Ошибка загрузки событий',
      statusReverted: 'Статус события успешно отменен',
      revertError: 'Ошибка отмены статуса события',
      finalCompletedMsg: 'Событие окончательно завершено',
      finalCompleteError: 'Ошибка окончательного завершения события',
      deleteError: 'Ошибка удаления события',
      restoreError: 'Ошибка восстановления события',
      purgeError: 'Ошибка окончательного удаления',
      category: 'Категория',
      addressLabel: 'Адрес',
      notSpecified: 'Не указан',
      priorityLabel: 'Приоритет',
      createdLabel: 'Создано',
      assignedLabel: 'Назначено',
      deletedBy: 'Удалил',
      finalCompletedLabel: 'Окончательно завершено'
    }
  };

  const t = translations[language];

  useEffect(() => {
    loadEvents();
    loadTrash();
  }, []);

  useEffect(() => {
    filterEvents();
  }, [events, searchTerm, statusFilter]);

  const loadEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
    } catch (err) {
      console.error('Error loading events:', err);
      setError(t.loadError);
    } finally {
      setLoading(false);
    }
  };

  const filterEvents = () => {
    let filtered = events;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(event => 
        event.description_ru.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.description_kk.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter && statusFilter !== 'all') {
      filtered = filtered.filter(event => event.status === statusFilter);
    }

    setFilteredEvents(filtered);
  };

  const revertEventStatus = async (eventId) => {
    if (!window.confirm(t.confirmRevert)) return;

    try {
      await axios.post(`${API}/admin/events/${eventId}/revert-status`, {});
      
      await loadEvents();
      alert(t.statusReverted);
    } catch (err) {
      console.error('Error reverting event status:', err);
      setError(t.revertError);
    }
  };

  const finalCompleteEvent = async (eventId) => {
    if (!window.confirm(t.confirmFinalComplete)) return;

    try {
      await axios.post(`${API}/admin/events/${eventId}/final-complete`, {});
      
      await loadEvents();
      alert(t.finalCompletedMsg);
    } catch (err) {
      console.error('Error final completing event:', err);
      setError(t.finalCompleteError);
    }
  };

  const loadTrash = async () => {
    try {
      const response = await axios.get(`${API}/admin/trash`);
      setTrashEvents(response.data.events || []);
    } catch (err) {
      console.error('Error loading trash:', err);
    }
  };

  const deleteEvent = async (eventId) => {
    if (!window.confirm(t.confirmDelete)) return;

    try {
      await axios.delete(`${API}/events/${eventId}`);

      await loadEvents();
      await loadTrash();
      alert(t.deleted);
    } catch (err) {
      console.error('Error deleting event:', err);
      setError(t.deleteError);
    }
  };

  const restoreEvent = async (eventId) => {
    if (!window.confirm(t.confirmRestore)) return;

    try {
      await axios.post(`${API}/admin/trash/${eventId}/restore`, {});
      await loadEvents();
      await loadTrash();
      alert(t.restored);
    } catch (err) {
      console.error('Error restoring event:', err);
      setError(t.restoreError);
    }
  };

  const purgeEvent = async (eventId) => {
    if (!window.confirm(t.confirmPurge)) return;

    try {
      await axios.delete(`${API}/admin/trash/${eventId}`);
      await loadTrash();
      alert(t.purged);
    } catch (err) {
      console.error('Error purging event:', err);
      setError(t.purgeError);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'completed': return 'status-completed';
      case 'final_completed': return 'status-final-completed';
      case 'in_progress': return 'status-in-progress';
      case 'pending': return 'status-pending';
      case 'rejected': return 'status-rejected';
      default: return 'status-default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'new': return t.newStatus;
      case 'assigned': return t.assigned;
      case 'completed': return t.completed;
      case 'final_completed': return t.finalCompleted;
      case 'in_progress': return t.inProgress;
      case 'rejected': return t.rejected;
      default: return status;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString(dateLocale(language), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="admin-event-manager-overlay">
        <div className="admin-event-manager">
          <div className="loading">{t.loading}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-event-manager-overlay">
      <div className="admin-event-manager">
        <div className="admin-event-header">
          <h2>{t.title}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {error && (
          <div className="error-message">{error}</div>
        )}

        <div className="aem-tabs">
          <button
            className={`aem-tab ${view === 'events' ? 'active' : ''}`}
            onClick={() => setView('events')}
            data-testid="aem-tab-events"
          >
            {t.eventsTab}
          </button>
          <button
            className={`aem-tab ${view === 'trash' ? 'active' : ''}`}
            onClick={() => { setView('trash'); loadTrash(); }}
            data-testid="aem-tab-trash"
          >
            🗑 {t.trash} ({trashEvents.length})
          </button>
        </div>

        {view === 'events' && (
        <>
        <div className="admin-event-controls">
          <div className="search-controls">
            <input
              type="text"
              placeholder={t.searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="status-filter"
            >
              <option value="all">{t.allStatuses}</option>
              <option value="new">{t.newStatus}</option>
              <option value="assigned">{t.assigned}</option>
              <option value="in_progress">{t.inProgress}</option>
              <option value="completed">{t.completed}</option>
              <option value="final_completed">{t.finalCompleted}</option>
              <option value="rejected">{t.rejected}</option>
            </select>
          </div>
        </div>

        <div className="admin-events-list">
          {filteredEvents.length === 0 ? (
            <div className="no-events">{t.noEvents}</div>
          ) : (
            filteredEvents.map(event => (
              <div key={event.id} className="admin-event-item">
                <div className="event-info">
                  <div className="event-header">
                    <h4>{(language === 'kk' ? event.description_kk : event.description_ru) || event.description_ru || event.description_kk}</h4>
                    <span className={`status-badge ${getStatusBadgeClass(event.status)}`}>
                      {getStatusText(event.status)}
                    </span>
                  </div>
                  
                  <div className="event-details">
                    <p><strong>{t.category}:</strong> {event.category}</p>
                    <p><strong>{t.addressLabel}:</strong> {event.address || t.notSpecified}</p>
                    <p><strong>{t.priorityLabel}:</strong> {event.priority}/5</p>
                    <p><strong>{t.createdLabel}:</strong> {formatDate(event.created_at)}</p>
                    {event.assigned_to && (
                      <p><strong>{t.assignedLabel}:</strong> {event.assigned_to}</p>
                    )}
                  </div>
                </div>

                <div className="event-actions">
                  {event.status === 'completed' && (
                    <>
                      <button
                        className="revert-btn"
                        onClick={() => revertEventStatus(event.id)}
                      >
                        ↩️ {t.revertStatus}
                      </button>
                      <button
                        className="final-complete-btn"
                        onClick={() => finalCompleteEvent(event.id)}
                      >
                        ✅ {t.finalComplete}
                      </button>
                    </>
                  )}
                  
                  {event.status === 'final_completed' && (
                    <span className="final-completed-label">
                      ✅ {t.finalCompletedLabel}
                    </span>
                  )}

                  <button
                    className="delete-event-btn"
                    onClick={() => deleteEvent(event.id)}
                    data-testid={`delete-event-${event.id}`}
                  >
                    🗑 {t.deleteEvent}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
        </>
        )}

        {view === 'trash' && (
          <div className="admin-events-list" data-testid="aem-trash-list">
            <div className="trash-note">💡 {t.trashNote}</div>
            {trashEvents.length === 0 ? (
              <div className="no-events">{t.trashEmpty}</div>
            ) : (
              trashEvents.map(event => (
                <div key={event.id} className="admin-event-item trash-item" data-testid={`trash-event-${event.id}`}>
                  <div className="event-info">
                    <div className="event-header">
                      <h4>{(language === 'kk' ? event.description_kk : event.description_ru) || event.description_ru || event.description_kk}</h4>
                      <span className="status-badge status-rejected">
                        {t.deletedAt}: {formatDate(event.deleted_at)}
                      </span>
                    </div>

                    <div className="event-details">
                      <p><strong>{t.category}:</strong> {event.category}</p>
                      <p><strong>{t.createdLabel}:</strong> {formatDate(event.created_at)}</p>
                      {event.deleted_by_name && (
                        <p><strong>{t.deletedBy}:</strong> {event.deleted_by_name}</p>
                      )}
                    </div>
                  </div>

                  <div className="event-actions">
                    <button
                      className="restore-btn"
                      onClick={() => restoreEvent(event.id)}
                      data-testid={`restore-event-${event.id}`}
                    >
                      ↩️ {t.restore}
                    </button>
                    <button
                      className="delete-event-btn"
                      onClick={() => purgeEvent(event.id)}
                      data-testid={`purge-event-${event.id}`}
                    >
                      ❌ {t.purge}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEventManager;